/**
 * React Query hooks for Logfire token data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createLogfireToken,
  listLogfireTokens,
  deleteLogfireToken,
  validateLogfireToken,
} from "@/services/logfireService";
import type { LogfireTokenCreate } from "@/types/integrations";

/**
 * Hook to fetch a paginated list of Logfire tokens.
 */
export function useLogfireTokens(page: number = 1, itemsPerPage: number = 10) {
  return useQuery({
    queryKey: ["logfire-tokens", page, itemsPerPage],
    queryFn: () => listLogfireTokens(page, itemsPerPage),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to create a new Logfire token.
 */
export function useCreateLogfireToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LogfireTokenCreate) => createLogfireToken(data),
    onSuccess: () => {
      // Invalidate and refetch all tokens queries
      queryClient.invalidateQueries({
        queryKey: ["logfire-tokens"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to delete a Logfire token.
 */
export function useDeleteLogfireToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: string) => deleteLogfireToken(tokenId),
    onSuccess: () => {
      // Invalidate and refetch all tokens queries
      queryClient.invalidateQueries({
        queryKey: ["logfire-tokens"],
        refetchType: "all",
      });
    },
  });
}

/**
 * Hook to re-validate a Logfire token.
 */
export function useValidateLogfireToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: string) => validateLogfireToken(tokenId),
    onSuccess: () => {
      // Invalidate and refetch all tokens queries with updated validation status
      queryClient.invalidateQueries({
        queryKey: ["logfire-tokens"],
        refetchType: "all",
      });
    },
  });
}
